import { useQuery } from '@tanstack/react-query'
import api from '~utils/http'
import {
  isIntakeSpecV2,
  type ConcernMeta,
  type IntakeSpecV2,
} from '~containers/app/ask-a-vet/intake-v2'

// 서버가 내려주는 필수 문진 질문지 (DRT-7112). drtail-owner-web 의 use-intake-spec 과 같은 형태.
export type IntakeOption = Readonly<{ value: string; label: string }>
export type IntakeQuestion = Readonly<{
  id: string
  question: string
  multiple: boolean
  options: IntakeOption[]
  // 앞 질문의 답이 이 값 중 하나면 이 질문은 묻지 않는다 ({질문 id: [값...]}). 서버 is_skipped 와 같은 조건
  skip_if?: Record<string, string[]>
}>
export type IntakeSpec = Readonly<{
  version: number
  safety: IntakeQuestion[]
  modules: Record<string, IntakeQuestion[]>
  new_concerns?: Readonly<{ value: string; label: string; description: string }>[]
  // 주요 증상 메뉴 순서(기존·신규 증상 값, others 는 맨 끝). 없으면 예전 순서
  concern_order?: string[]
  // 주요 증상 메뉴 (DRT-7185). 없으면 예전 목록
  concern_meta?: ConcernMeta[]
}>

// v2 질문지(DRT-7186) 또는 v1 폴백
export type AnyIntakeSpec = IntakeSpec | IntakeSpecV2

const INTAKE_SPEC_PATH = '/app/consultations/intake_spec/'

/** v1 질문지만 (v2 를 받았으면 undefined). v1 화면(4b_safety 의 v1 경로·기존 증상 화면)용 */
export const getIntakeSpecV1 = (spec: AnyIntakeSpec | undefined): IntakeSpec | undefined =>
  spec && !isIntakeSpecV2(spec) ? spec : undefined

/** v2 질문지만 (서버가 v1 을 줬거나 못 받았으면 undefined → v1 흐름) */
export const getIntakeSpecV2 = (spec: AnyIntakeSpec | undefined): IntakeSpecV2 | undefined =>
  isIntakeSpecV2(spec) ? spec : undefined

/**
 * pinned: 이 상담의 문진 버전을 이미 정했으면 true (진행 중에는 질문지를 다시 받지 않는다).
 * v2 를 먼저 요청하고(?version=2), 요청이 실패하면 v1 을 받는다.
 * 구 서버는 version 을 무시하고 v1 을 주므로 그대로 v1 흐름으로 쓴다 (v1 경로는 다음 릴리스에 삭제).
 */
const useIntakeSpec = ({ pinned = false }: { pinned?: boolean } = {}) =>
  useQuery({
    queryKey: ['consultation', 'intake-spec', 'v2'],
    queryFn: async (): Promise<AnyIntakeSpec> => {
      try {
        return (
          await api.get<AnyIntakeSpec>(INTAKE_SPEC_PATH, {
            params: { version: 2 },
            withCredentials: true,
          })
        ).data
      } catch {
        return (await api.get<IntakeSpec>(INTAKE_SPEC_PATH, { withCredentials: true })).data
      }
    },
    // 문진 버전을 정한 뒤(상담 진행 중)에는 다시 받지 않는다: 중간에 질문지가 바뀌지 않게
    staleTime: pinned ? Infinity : 60 * 60 * 1000,
  })

export default useIntakeSpec
